// Motion for the two finalists: Soft Chat o and In conversation.
// Wordmark geometry matches LogoConcepts.tsx (456 wide, x-height 40→140, stroke 24).

const C = {
  black: { fg: "fill-paper", fgS: "stroke-paper", acc: "fill-sun", bg: "fill-ink", bgS: "stroke-ink" },
  white: { fg: "fill-ink", fgS: "stroke-ink", acc: "fill-ink", bg: "fill-paper", bgS: "stroke-paper" },
  yellow: { fg: "fill-ink", fgS: "stroke-ink", acc: "fill-ink", bg: "fill-sun", bgS: "stroke-sun" },
};

export const SOFT_TAIL = "M22 108 C21 128 15 142 4 153 C23 154 39 147 52 135 Z";
const BACK_TAIL = "M124 28 C128 16 134 8 142 2 C128 2 116 8 108 18 Z";

const STEMS = [
  { x: 134, y: 0, h: 140 },
  { x: 182, y: 0, h: 140 },
  { x: 230, y: 40, h: 100 },
  { x: 374, y: 40, h: 100 },
  { x: 422, y: 40, h: 100 },
];
const DOTS = [242, 434];
const rise = (delay) => ({
  transformBox: "fill-box",
  transformOrigin: "50% 100%",
  animationDelay: `${delay}ms`,
});

/** "lli ai" building up: stems rise in turn, then the a draws, then dots land. */
function RestBuild({ tone, start }) {
  const c = C[tone];
  return (
    <>
      {STEMS.map((s, i) => (
        <rect
          key={s.x}
          x={s.x}
          y={s.y}
          width="24"
          height={s.h}
          rx="12"
          className={`${c.fg} animate-olli-rise motion-reduce:animate-none`}
          style={rise(start + i * 70 + (i > 2 ? 160 : 0))}
        />
      ))}
      <circle
        cx="348"
        cy="90"
        r="38"
        fill="none"
        strokeWidth="24"
        strokeDasharray="238.76"
        transform="rotate(-90 348 90)"
        className={`${c.fgS} animate-olli-draw motion-reduce:animate-none`}
        style={{ animationDuration: "420ms", animationDelay: `${start + 230}ms` }}
      />
      {DOTS.map((cx, i) => (
        <circle
          key={cx}
          cx={cx}
          cy="12"
          r="13"
          className={`${c.fg} animate-olli-drop motion-reduce:animate-none`}
          style={{ animationDelay: `${start + 330 + i * 280}ms` }}
        />
      ))}
    </>
  );
}

// --- Soft Chat o ---------------------------------------------------------

/** Splash, ~2.1s: tail pops, o draws from the tail, o slides left, lli ai builds, dots land. */
export function SoftSplash({ tone, className = "" }) {
  const c = C[tone];
  return (
    <svg viewBox="-4 -8 464 168" className={className} role="img" aria-label="Olli AI">
      <g className="animate-olli-slide motion-reduce:animate-none">
        <circle
          cx="60"
          cy="90"
          r="38"
          fill="none"
          strokeWidth="24"
          strokeDasharray="238.76"
          transform="rotate(125 60 90)"
          className={`${c.fgS} animate-olli-draw motion-reduce:animate-none`}
        />
        <path
          d={SOFT_TAIL}
          className={`${c.acc} animate-olli-tail motion-reduce:animate-none`}
          style={{ transformBox: "fill-box", transformOrigin: "80% 20%" }}
        />
      </g>
      <RestBuild tone={tone} start={1150} />
    </svg>
  );
}

/** Loader, loops 1s: an arc runs round the o, tail stays put. */
export function SoftLoader({ size, tone }) {
  const c = C[tone];
  const sw = size <= 20 ? 20 : 24;
  return (
    <svg viewBox="-2 30 124 124" width={size} height={size} role="status" aria-label="Loading" className="shrink-0">
      <circle cx="60" cy="90" r="38" fill="none" strokeWidth={sw} className={`${c.fgS} opacity-15`} />
      <g className="animate-olli-spin motion-reduce:animate-olli-pulse" style={{ transformOrigin: "60px 90px" }}>
        <circle
          cx="60"
          cy="90"
          r="38"
          fill="none"
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray="72 166.76"
          className={c.fgS}
        />
      </g>
      <path d={SOFT_TAIL} className={c.acc} />
    </svg>
  );
}

// --- In conversation -----------------------------------------------------

function BackBubble({ tone }) {
  const c = C[tone];
  return (
    <>
      <circle cx="102" cy="48" r="34" className={c.acc} />
      <path d={BACK_TAIL} className={c.acc} />
    </>
  );
}
function FrontBubble({ tone }) {
  const c = C[tone];
  return (
    <>
      <circle cx="60" cy="90" r="48" className={c.bg} />
      <circle cx="60" cy="90" r="50" fill="none" strokeWidth="12" className={c.bgS} />
      <path d={SOFT_TAIL} strokeWidth="18" strokeLinejoin="round" className={`${c.bg} ${c.bgS}`} />
      <circle cx="60" cy="90" r="38" fill="none" strokeWidth="24" className={c.fgS} />
      <path d={SOFT_TAIL} className={c.fg} />
    </>
  );
}

/** Splash, ~2s: yellow bubble pops in (patient), chat o pops in front (Olli replies), then "olli ai" builds. */
export function ConversationSplash({ tone, className = "" }) {
  const c = C[tone];
  return (
    <svg viewBox="-8 -42 676 208" className={className} role="img" aria-label="Olli AI">
      <g transform="translate(0 -36) scale(1.3)">
        <g className="animate-olli-pop motion-reduce:animate-none" style={{ transformOrigin: "102px 48px" }}>
          <BackBubble tone={tone} />
        </g>
        <g
          className="animate-olli-pop motion-reduce:animate-none"
          style={{ transformOrigin: "40px 120px", animationDelay: "420ms" }}
        >
          <FrontBubble tone={tone} />
        </g>
      </g>
      <g transform="translate(200 0)">
        <circle
          cx="60"
          cy="90"
          r="38"
          fill="none"
          strokeWidth="24"
          strokeDasharray="238.76"
          transform="rotate(-90 60 90)"
          className={`${c.fgS} animate-olli-draw motion-reduce:animate-none`}
          style={{ animationDuration: "460ms", animationDelay: "820ms" }}
        />
        <RestBuild tone={tone} start={1000} />
      </g>
    </svg>
  );
}

/** Static logo: symbol + "olli ai". Final Olli AI logo. */
export function ConversationWordmark({ tone, className = "" }) {
  const c = C[tone];
  return (
    <svg viewBox="-8 -42 676 208" className={className} role="img" aria-label="Olli AI">
      <g transform="translate(0 -36) scale(1.3)">
        <BackBubble tone={tone} />
        <FrontBubble tone={tone} />
      </g>
      <g transform="translate(200 0)">
        <circle cx="60" cy="90" r="38" fill="none" strokeWidth="24" className={c.fgS} />
        {STEMS.map((s) => (
          <rect key={s.x} x={s.x} y={s.y} width="24" height={s.h} rx="12" className={c.fg} />
        ))}
        <circle cx="348" cy="90" r="38" fill="none" strokeWidth="24" className={c.fgS} />
        {DOTS.map((cx) => (
          <circle key={cx} cx={cx} cy="12" r="13" className={c.fg} />
        ))}
      </g>
    </svg>
  );
}

/** Loader, loops 1.2s: the two bubbles take turns, like a back-and-forth chat. */
export function ConversationLoader({ size, tone }) {
  return (
    <svg viewBox="-8 -6 156 160" width={size} height={size} role="status" aria-label="Loading" className="shrink-0">
      <g className="animate-olli-talk motion-reduce:animate-olli-pulse" style={{ transformOrigin: "102px 48px" }}>
        <BackBubble tone={tone} />
      </g>
      <g
        className="animate-olli-talk motion-reduce:animate-olli-pulse"
        style={{ transformOrigin: "60px 90px", animationDelay: "-600ms" }}
      >
        <FrontBubble tone={tone} />
      </g>
    </svg>
  );
}

/** The two bubbles alone, static. For the collapsed menu and small spaces. */
export function ConversationMark({ tone, className = "" }) {
  return (
    <svg viewBox="-6 -4 154 164" className={className} role="img" aria-label="Olli AI">
      <BackBubble tone={tone} />
      <FrontBubble tone={tone} />
    </svg>
  );
}
